import type { Component } from '../router';
import { ApiError } from '../services/api';
import {
  getShopProduct,
  listShopProducts,
  type Product,
  type ProductDetail,
} from '../api/client';
import { addToCart } from '../services/cart';
import { formatPrice } from './ProductsPage';
import { productJsonLd, injectProductJsonLd, removeProductJsonLd } from '../seo/structuredData';
import {
  ensureMotifDefs,
  frondField,
  curl,
  formOf,
  formLabel,
  imagesOf,
  productPhoto,
} from '../components/shop/frondArt';
import { fdaDisclaimer, withClaimMarker } from '../components/shop/disclaimer';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// ProductDetailPage renders one product loaded from GET /api/shop/products/{slug}.
// Public read. Built under the one-motif direction: the drawn bottle in a leaf
// field (OI-002), the benefits* with the FDA disclaimer beside them, the
// supplement facts and age-based dosing, and a buy control (add-to-cart for a
// signed-in visitor; sign-in otherwise). A form switcher links the sibling SKUs.
// Emits schema.org structured data (CONTENT-004).
export class ProductDetailPage implements Component {
  private root: HTMLElement | null = null;
  private controller: AbortController | null = null;
  private siblingController: AbortController | null = null;
  private switcher: HTMLElement | null = null;
  private readonly slug: string;

  constructor(slug: string) {
    this.slug = slug;
  }

  mount(host: HTMLElement): void {
    ensureMotifDefs();
    const section = el('section', 'shop pdp');
    section.appendChild(el('p', 'shop__status', 'Loading…'));
    host.appendChild(section);
    this.root = section;
    void this.load(section);
  }

  unmount(): void {
    this.controller?.abort();
    this.siblingController?.abort();
    this.controller = null;
    this.siblingController = null;
    removeProductJsonLd();
    this.root?.remove();
    this.root = null;
  }

  private async load(section: HTMLElement): Promise<void> {
    this.controller = new AbortController();
    try {
      const product = await getShopProduct(this.slug, this.controller.signal);
      section.innerHTML = '';
      section.appendChild(this.render(product));
      injectProductJsonLd(productJsonLd(product, location.origin));
      void this.loadSiblings();
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      section.innerHTML = '';
      if (err instanceof ApiError && err.status === 404) {
        section.appendChild(el('h1', 'pdp__title', 'Product not found'));
        const back = el('a', 'buy-link', 'Back to products') as HTMLAnchorElement;
        back.href = '/products';
        section.appendChild(back);
        return;
      }
      section.appendChild(
        el('p', 'shop__status shop__status--error', 'Could not load this product. Please try again.'),
      );
    }
  }

  private render(product: ProductDetail): HTMLElement {
    const frag = document.createDocumentFragment();

    const crumb = el('nav', 'pdp__crumb');
    const crumbLink = el('a', undefined, 'Products') as HTMLAnchorElement;
    crumbLink.href = '/products';
    crumb.append(crumbLink, document.createTextNode(` / ${product.name}`));

    const hero = el('div', 'pdp__hero');
    // Real photos when the product carries them (first image is the hero, the
    // rest become a thumbnail gallery); otherwise the drawn frond art stands in
    // (OI-002 — so C+ Liquid stays drawn until liquid photography exists).
    const media = el('div', 'pdp__media');
    const photos = imagesOf(product);
    if (photos.length > 0) {
      media.classList.add('pdp__media--photo');
      media.appendChild(this.gallery(photos, product.name));
    } else {
      media.appendChild(frondField(formOf(product)));
    }

    const info = el('div', 'pdp__info');
    info.appendChild(el('h1', 'pdp__title', product.name));
    if (product.description) {
      info.appendChild(el('p', 'pdp__promise', withClaimMarker(product.description)));
    }

    // Form switcher placeholder — filled by loadSiblings() when the catalog
    // resolves (the current SKU is always present as a pressed pill).
    this.switcher = el('div', 'form-toggle');
    this.switcher.setAttribute('role', 'group');
    this.switcher.setAttribute('aria-label', 'Choose a form');
    this.switcher.hidden = true;
    info.appendChild(this.switcher);

    const priceRow = el('div', 'pdp__pricerow');
    priceRow.append(
      el('span', 'price', formatPrice(product.priceCents, product.currency)),
      el('span', undefined, formLabel(formOf(product))),
    );
    priceRow.lastElementChild?.setAttribute('style', 'color: var(--color-text-secondary)');
    info.appendChild(priceRow);

    const benefits = (product.benefits ?? []).filter(Boolean);
    if (benefits.length > 0) {
      const list = el('ul', 'benefits');
      for (const b of benefits) {
        const item = el('li', 'benefits__item');
        item.append(curl('frond-bullet'), document.createTextNode(` ${withClaimMarker(b)}`));
        list.appendChild(item);
      }
      info.appendChild(list);
    }

    info.appendChild(this.actions(product));
    info.appendChild(fdaDisclaimer());

    hero.append(media, info);
    frag.append(crumb, hero);

    const facts = this.facts(product);
    if (facts) frag.appendChild(facts);

    const wrap = el('div');
    wrap.appendChild(frag);
    return wrap;
  }

  // gallery renders the hero photo (the first image) and, when there is more
  // than one, a thumbnail strip whose buttons swap the hero in place. The hero
  // carries the product name as alt; the thumbnails are decorative and named by
  // their buttons, so a screen reader is not told the same image twice.
  private gallery(urls: string[], name: string): HTMLElement {
    const wrap = el('div', 'pdp__gallery');
    const hero = productPhoto(urls[0], name, 'product-photo product-photo--hero');
    wrap.appendChild(hero);
    if (urls.length < 2) return wrap;

    const strip = el('div', 'pdp__thumbs');
    strip.setAttribute('role', 'group');
    strip.setAttribute('aria-label', `${name} photos`);
    const thumbs: HTMLButtonElement[] = [];
    urls.forEach((url, i) => {
      const btn = el('button', 'pdp__thumb') as HTMLButtonElement;
      btn.type = 'button';
      btn.setAttribute('aria-label', `View photo ${i + 1} of ${urls.length}`);
      btn.setAttribute('aria-pressed', String(i === 0));
      btn.appendChild(productPhoto(url, '', 'pdp__thumb-img'));
      btn.addEventListener('click', () => {
        hero.src = url;
        thumbs.forEach((b, j) => b.setAttribute('aria-pressed', String(j === i)));
      });
      thumbs.push(btn);
      strip.appendChild(btn);
    });
    wrap.appendChild(strip);
    return wrap;
  }

  // loadSiblings fetches the catalog and, when there is more than one SKU, fills
  // the form switcher with real links to the siblings. Non-blocking: a failure
  // simply leaves the switcher hidden.
  private async loadSiblings(): Promise<void> {
    this.siblingController = new AbortController();
    let list: Product[];
    try {
      const res = await listShopProducts({}, this.siblingController.signal);
      list = (res.products ?? []).filter((p) => p.slug);
    } catch {
      return;
    }
    const sw = this.switcher;
    if (!sw || list.length < 2) return;
    sw.innerHTML = '';
    for (const p of list) {
      const label = formLabel(formOf(p));
      if (p.slug === this.slug) {
        const btn = el('span', 'form-toggle__opt', label);
        btn.setAttribute('aria-pressed', 'true');
        sw.appendChild(btn);
      } else {
        const a = el('a', 'form-toggle__opt', label) as HTMLAnchorElement;
        a.href = `/products/${encodeURIComponent(p.slug)}`;
        a.setAttribute('aria-pressed', 'false');
        sw.appendChild(a);
      }
    }
    sw.hidden = false;
  }

  // facts renders the supplement-facts panel and age-based dosing when the
  // product carries them.
  private facts(product: ProductDetail): HTMLElement | null {
    const hasFacts = Boolean(product.supplementFacts);
    const hasDose = Boolean(product.dosage);
    if (!hasFacts && !hasDose) return null;

    const section = el('section', 'pdp-section');
    section.setAttribute('aria-label', 'Supplement facts and how to use');
    const head = el('div', 'pdp-section__head');
    head.append(el('h2', 'pdp-section__title', 'Supplement facts & how to use'));
    section.appendChild(head);

    const grid = el('div', 'facts');
    if (hasFacts) {
      const panel = el('div', 'facts-panel');
      panel.append(
        el('p', 'facts-panel__title', 'Supplement Facts'),
        el('p', 'facts-panel__raw', product.supplementFacts ?? ''),
      );
      grid.appendChild(panel);
    }
    if (hasDose) {
      const howto = el('div', 'howto');
      const row = el('div', 'howto__row');
      row.append(el('span', 'howto__who', 'How to use'), el('span', undefined, product.dosage ?? ''));
      howto.appendChild(row);
      grid.appendChild(howto);
    }
    section.appendChild(grid);
    // The supplement-facts panel is its own claim surface, below the hero
    // disclaimer — Brand §7 requires the disclaimer to travel with every claim.
    section.appendChild(fdaDisclaimer());
    return section;
  }

  private actions(product: ProductDetail): HTMLElement {
    const row = el('div', 'pdp__actions');

    // Add to cart is open to everyone — a signed-out visitor builds an anonymous
    // device cart (merged into their account on sign-in); the sign-in gate lives
    // at checkout, not here (RAD §7: browse freely, sign in to buy).
    const add = el('button', 'buy-btn');
    add.type = 'button';
    add.append(curl('buy-btn__curl'), document.createTextNode(' Add to cart'));
    add.disabled = !product.inStock;

    const status = el('p', 'pdp__cart-status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    add.addEventListener('click', () => {
      void (async () => {
        add.disabled = true;
        status.textContent = 'Adding…';
        try {
          const count = await addToCart(product.slug, 1);
          status.textContent = `Added — ${count} item${count === 1 ? '' : 's'} in your cart.`;
        } catch {
          status.textContent = 'Could not add to cart. Please try again.';
        } finally {
          add.disabled = !product.inStock;
        }
      })();
    });

    row.append(add, status);
    return row;
  }
}
